import { createDirectStore, } from 'direct-vuex';
import Vue from 'vue';
import Vuex from 'vuex';
import { BankDetailsInput, } from '~/api/self-serve-client';
import { formatSortCode, } from '~/utils/bankDetailsFormattingMethods';
import { AddressData, IMovingDetails, } from '~/utils/models';
import { isDate, isObject, } from '~/utils/models/isObject';
import { initialiseStoreValue, } from '~/utils/store';
import alertModule from '~/utils/store/alert-module';
import globalConfigModule from '~/utils/store/global-config-module';

Vue.use(Vuex);

const UserDetailsStorageKey = 'userDetails';
const MovingDetailsStorageKey = 'movingDetails';
const apiDataForSuccessStorageKey = 'apiDataForSuccess';
const PaymentDetailsStorageKey = 'paymentDetails';

interface MasterData {
	masterId: number;
	text?: string;
	value?: string;
}

export type RecommendedAmountThreshold = {
	recommendedAmount: number | null,
	minimumAmount: number | null,
	maximumAmount: number | null,
};

export interface UserDetails {
	customerNumber: string | null | undefined;
	customerTitle: string | null;
	customerLastName: string;
	customerEmail: string;
	customerFirstName: string;
	customerMiddleName: string;
	selectedCountryCode: string | null;
	phoneNumberOption1: string;
	phoneCodeOption1: string;
	selectedPhoneTypeOption1: string;
	selectedDate: Date | null;
	paymentRef: string | null;
	propertyChargedOn: string | null;
	showNewAccountDetails: boolean | null;
	isNewCustomer?:boolean | null;
	directDebitSetupFailureReason: 'validation' | 'paymentscheme' | 'other' | null;
	isMoveInForcefullySucceeded: boolean | null;
	customerServiceFlag: string | null;
	contractId: string;
	isMetered: boolean | undefined | null;
}

export interface DirectDebitPayments {
	isDirectDebit: true;
	paymentDate: number;
	paymentFrequency: string;
	ddPaymentAmount: number | null;
}

export interface BilledPayments {
	isDirectDebit: false;
}

export type PaymentDetails = DirectDebitPayments | BilledPayments;

function isUserDetails(input: unknown): input is UserDetails {
	return (
		true &&
		isObject(input) &&
		typeof input.customerLastName === 'string' &&
		typeof input.customerEmail === 'string' &&
		typeof input.customerFirstName === 'string' &&
		typeof input.customerMiddleName === 'string' &&
		typeof input.phoneNumberOption1 === 'string' &&
		true
	);
}

function isBilledPayments(input: unknown): input is BilledPayments {
	return isObject(input)
		&& input.isDirectDebit === false;
}

function isDirectDebitPayments(input: unknown): input is DirectDebitPayments {
	return isObject(input)
		&& input.isDirectDebit === true
		&& typeof input.paymentDate === 'number'
		&& typeof input.paymentFrequency === 'string';
}

function isPaymentDetails(input: unknown): input is PaymentDetails {
	return isBilledPayments(input) || isDirectDebitPayments(input);
}

function isMovingDetails(input: unknown): input is IMovingDetails {
	return (
		true &&
		isObject(input) &&
		isObject(input.newAddress) &&
		isDate(input.moveInDate) &&
		typeof input.isCorrespondenceAddress === 'boolean' &&
		(input.isCorrespondenceAddress === true || isObject(input.correspondenceAddress)) &&
		typeof input.occupancyType === 'string' &&
		typeof input.noPersons === 'number' &&
		true
	);
}

function initialiseUserDetails(): UserDetails {
	return initialiseStoreValue(UserDetailsStorageKey, isUserDetails, () => ({
		customerNumber: null,
		customerTitle: null,
		customerEmail: '',
		customerLastName: '',
		customerFirstName: '',
		customerMiddleName: '',
		selectedCountryCode: null,
		phoneNumberOption1: '',
		phoneCodeOption1: '',
		selectedPhoneTypeOption1: '',
		selectedDate: null,
		paymentRef: null,
		propertyChargedOn: null,
		showNewAccountDetails: null,
		isNewCustomer: null,
		directDebitSetupFailureReason: null,
		customerServiceFlag: null,
		isMoveInForcefullySucceeded: null,
		// paymentDateInApiDateFormat: null,
		contractId: '',
		isMetered: false,
	}), (parsed) => {
		// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
		parsed.selectedDate = parsed.selectedDate ? new Date(parsed.selectedDate) : null;
		// eslint-disable-next-line @typescript-eslint/no-unsafe-return
		return parsed;
	});
}

function initialiseMovingDetails() {
	return initialiseStoreValue(MovingDetailsStorageKey, isMovingDetails, (): IMovingDetails => ({
		newAddress: null,
		moveInDate: null,
		isCorrespondenceAddress: true,
		correspondenceAddress: null,
		occupancyType: 'Owner' as const,
		noPersons: null,
		personsToDisplayText: null,
	}), (parsed) => {
		// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
		parsed.moveInDate = parsed.moveInDate ? new Date(parsed.moveInDate) : null;
		// eslint-disable-next-line @typescript-eslint/no-unsafe-return
		return parsed;
	});
}

function initialisePaymentDetails(): PaymentDetails {
	return initialiseStoreValue(PaymentDetailsStorageKey, isPaymentDetails, () => ({
		isDirectDebit: true,
		paymentDate: 1,
		paymentFrequency: 'M',
		ddPaymentAmount: null,
	}));
}

const { store, } = createDirectStore({
	state: {
		currentStep: null as number | null,
		lastStep: null as number | null,
		showSpinner: false,
		userDetails: initialiseUserDetails(),
		editMode: false,
		movingDetails: initialiseMovingDetails(),
		addressList: [] as AddressData[],
		addressListC: [] as AddressData[],
		paymentDetails: initialisePaymentDetails(),
		bankDetails: null as BankDetailsInput | null,
		securityQuestionList: [] as MasterData[],
		reenterPaymentDetails: false,
		isManualAddressEntryForSupplyAddress: false,
		isOverlayWithSlotVisible: false,
		ddPaymentAmount: null as null | string | number,
		directDebitForcefullySucceeded: false,
		progressStepsPhase: 'phase1Steps',
		// paymentDateInApiDateFormat: null as null | string,
		recommendedAmountThreshold: {
			recommendedAmount: null,
			minimumAmount: null,
			maximumAmount: null,
		} as RecommendedAmountThreshold,
	},
	getters: {
		hasExistingUserDetails(state) {
			const {
				customerLastName,
				customerFirstName,
				selectedDate,
				selectedCountryCode,
				phoneNumberOption1,
			} = state.userDetails;
			return (
				(customerLastName || '').trim() !== '' &&
				(customerFirstName || '').trim() !== '' &&
				(selectedDate || '') &&
				(selectedCountryCode || '') &&
				(phoneNumberOption1 || '').trim() !== '' &&
				true
			);
		},
		hasExistingMoveingDetails(state) {
			return state.movingDetails !== undefined;
		},
		hasPaymentDetails(state) {
			return !state.paymentDetails.isDirectDebit || state.bankDetails !== null;
		},
		selectedPaymentMethod(state) {
			switch (state.paymentDetails.isDirectDebit) {
				case true:
					return 'Pay by Direct Debit';
				case false:
				default:
					return 'Pay on receipt of bill';
			}
		},
		formattedSortCode(state) {
			const sortCode = state.bankDetails?.sortCode;
			if (!sortCode) {
				return '';
			}

			return formatSortCode(sortCode);
		},
	},
	mutations: {
		showSpinner(state) {
			state.showSpinner = true;
		},
		hideSpinner(state) {
			state.showSpinner = false;
		},
		setStep(state, step: number | null) {
			state.currentStep = step;
		},
		setLastStep(state, step: number | null) {
			state.lastStep = step;
		},
		updateUserDetails(state, userDetails: UserDetails) {
			sessionStorage.setItem(
				UserDetailsStorageKey,
				JSON.stringify(userDetails)
			);
			state.userDetails = userDetails;
		},
		updateMovingDetails(state, movingDetails: IMovingDetails) {
			sessionStorage.setItem(
				MovingDetailsStorageKey,
				JSON.stringify(movingDetails)
			);
			state.movingDetails = movingDetails;
		},
		setBilledPayments(state) {
			const paymentDetails: BilledPayments = {
				isDirectDebit: false,
			};
			sessionStorage.setItem(PaymentDetailsStorageKey, JSON.stringify(paymentDetails));
			state.paymentDetails = paymentDetails;
			state.bankDetails = null;
		},
		setDirectDebitPayments(state, { paymentDetails, }: {
			paymentDetails: Omit<DirectDebitPayments, 'isDirectDebit'>,
			// bankDetails: BankDetailsInput,
		}) {
			const storeDetails = {
				isDirectDebit: true as const,
				...paymentDetails,
			};
			sessionStorage.setItem(PaymentDetailsStorageKey, JSON.stringify(storeDetails));
			state.paymentDetails = storeDetails;
			if (paymentDetails.ddPaymentAmount) {
				state.ddPaymentAmount = paymentDetails.ddPaymentAmount;
			}
			// state.bankDetails = bankDetails;
		},
		setBankDetail(state, detail) {
			state.bankDetails = detail.bankDetail;
		},
		updateScurityQuestionList(state, list: MasterData[]) {
			state.securityQuestionList = list;
		},
		setCustomerNumber(state, customerNumber: string | null | undefined) {
			state.userDetails.customerNumber = customerNumber;
			sessionStorage.setItem(UserDetailsStorageKey, JSON.stringify(state.userDetails));
		},
		updateCustomerDetail(state, obj: { customerNumber: string | null | undefined, paymentRef: string, customerServiceFlag: string, propertyChargedOn: string, showNewAccountDetails: boolean, directDebitSetupFailure: UserDetails['directDebitSetupFailureReason'], isMoveInForcefullySucceeded: boolean, contractId: string, isMetered: boolean | undefined | null, }) {
			state.userDetails.customerNumber = obj.customerNumber;
			state.userDetails.paymentRef = obj.paymentRef;
			state.userDetails.propertyChargedOn = obj.propertyChargedOn;
			state.userDetails.showNewAccountDetails = obj.showNewAccountDetails;
			state.userDetails.directDebitSetupFailureReason = obj.directDebitSetupFailure;
			state.userDetails.customerServiceFlag = obj.customerServiceFlag;
			state.userDetails.isMoveInForcefullySucceeded = obj.isMoveInForcefullySucceeded;
			state.userDetails.contractId = obj.contractId;
			state.userDetails.isMetered = obj.isMetered;
			sessionStorage.setItem(UserDetailsStorageKey, JSON.stringify(state.userDetails));
		},
		updateIsNewCustomer(state, arg: boolean) {
			state.userDetails.isNewCustomer = arg;
			sessionStorage.setItem(UserDetailsStorageKey, JSON.stringify(state.userDetails));
		},
		reset(state) {
			sessionStorage.removeItem(UserDetailsStorageKey);
			sessionStorage.removeItem(MovingDetailsStorageKey);
			sessionStorage.removeItem(apiDataForSuccessStorageKey);
			sessionStorage.removeItem(PaymentDetailsStorageKey);
			state.userDetails = initialiseUserDetails();
			state.movingDetails = initialiseMovingDetails();
			state.editMode = false;
			state.addressList = [];
			state.addressListC = [];
			state.lastStep = 0;
			state.paymentDetails = initialisePaymentDetails();
			state.securityQuestionList = [];
			state.isManualAddressEntryForSupplyAddress = false;
			state.isOverlayWithSlotVisible = false;
			state.directDebitForcefullySucceeded = false;
			// state.paymentDateInApiDateFormat = null;
		},
		updateEditMode(state, status: boolean) {
			state.editMode = status;
		},
		setAddressList(state, list: AddressData[]) {
			state.addressList = list;
		},
		setManualAddressEntryForSupplyAddress(state, status: boolean) {
			state.isManualAddressEntryForSupplyAddress = status;
		},
		setAddressListC(state, list: AddressData[]) {
			state.addressListC = list;
		},
		informReenterPaymentDetails(state, inform: boolean) {
			state.reenterPaymentDetails = inform;
		},
		showOverlayWithSlot(state) {
			state.isOverlayWithSlotVisible = true;
		},
		closeOverlayWithSlot(state) {
			state.isOverlayWithSlotVisible = false;
		},
		setRecommendedAmountThreshold(state, details:RecommendedAmountThreshold) {
			state.recommendedAmountThreshold = { ...details, };
		},
		setDDPaymentAmount(state, ddPaymentAmount) {
			state.ddPaymentAmount = ddPaymentAmount;
		},
		// setPaymentDateIntoApiDateFormat(state, date:string) {
		// 	state.paymentDateInApiDateFormat = date;
		// },
		directDebitForcefullySucceeded(state, directDebitForcefullySucceeded) {
			state.directDebitForcefullySucceeded = directDebitForcefullySucceeded;
		},
		setProgressSteps(state, progressStepsPhase) {
			state.progressStepsPhase = progressStepsPhase;
		},
	},
	modules: {
		alertModule,
		globalConfigModule,
	},
});

export const Store = store;
